import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { UrlAnimation } from "../../../../../../../components/Animation/UrlAnimation.tsx";
import { ErrorButton } from "../../../../../../../components/Button/ErrorButton.tsx";
import { Form } from "../../../../../../../components/Form/Form.tsx";
import { useForm } from "../../../../../../../components/Form/FormContext.ts";
import { Toggle } from "../../../../../../../components/Toggle/Toggle.tsx";
import { useAppQuery } from "../../../../../../../http/useAppQuery.ts";
import { STYLE_MODEL_TYPE } from "../../../../../../constants.ts";
import { PanelLayout } from "../../../../../components/Layout/PanelLayout.tsx";
import { LearnMoreLink } from "../../../../../components/LearnMoreLink.tsx";
import { PanelSection } from "../../../../../components/Panel/PanelSection.tsx";
import { ActionsBar } from "../../../../../components/Panel/Settings/ActionsBar.tsx";
import { ModelSelectionSection } from "../../../../../components/Panel/Settings/ModelSelectionSection.tsx";
import { PromptSection } from "../../../../../components/Panel/Settings/PromptSection.tsx";
import { SliderSubsection } from "../../../../../components/Panel/SliderSubsection.tsx";
import { useBoardUuid } from "../../../../../hooks/useBoardUuid.ts";
import { useSelectedAsset } from "../../../../../hooks/useSelectedAsset.ts";
import { scrollToEditionHistoryStore } from "../../../../../stores/scrollToEditionHistoryStore.ts";
import type { ContentDescription } from "../../../../../types.ts";
import { getErrorsFiltered } from "../../../../../utils/getErrorsFiltered.ts";
import { VARIATION_CREATIVITY_STRENGTH_PRESETS } from "./constants.ts";
import { useGenerateImageVariation } from "./hooks/useGenerateImageVariation.ts";
import { useImageVariationSettings } from "./hooks/useImageVariationSettings.ts";

export const VariationSettings = () => {
  const { mutation: variationMutate, isLoading: isGenerationLoading } =
    useGenerateImageVariation({
      onSuccess: () => {
        scrollToEditionHistoryStore.triggerScrollToEditionHistory();
      },
    });

  return (
    <Form
      schema={zVariationSettings}
      initialValues={{
        selectedAssetUuid: "",
        prompt: "",
        isSelectedImageSucceeded: false,
        areSelectedModelsValid: false,
      }}
      onSubmit={(values) => {
        // FIXME: make the mutation async
        variationMutate({
          assetUuid: values.selectedAssetUuid,
        });
        return Promise.resolve(true);
      }}
    >
      <VariationSettingsFormContent isGenerationLoading={isGenerationLoading} />
    </Form>
  );
};

const zVariationSettings = z.object({
  selectedAssetUuid: z
    .string({ required_error: "Please upload an image in the canvas" })
    .uuid(),
  prompt: z.string().min(1, { message: "Please enter a description" }),
  isSelectedImageSucceeded: z.boolean().refine((val) => val, {
    message: "Please wait for your image to be generated",
  }),
  areSelectedModelsValid: z.boolean().refine((val) => val, {
    message:
      "The selected models are not valid, please try again by reseting the selection.",
  }),
});
type VariationSettingsValues = z.input<typeof zVariationSettings>;

export const VariationSettingsFormContent = ({
  isGenerationLoading,
}: {
  isGenerationLoading: boolean;
}) => {
  const boardUuid = useBoardUuid();
  const { tool, settings, setSettings, areSelectedModelsValid, modelActions } =
    useImageVariationSettings(boardUuid);

  const { selectedAsset } = useSelectedAsset();

  const { data: initImageDescription } = useAppQuery<ContentDescription>({
    queryKey: selectedAsset
      ? `contents/${selectedAsset.content.uuid}/description`
      : null,
  });

  const { setValues, submit, useError } = useForm<VariationSettingsValues>();
  const missingAssetError = useError((v) => v.selectedAssetUuid);
  const modelsNotValidError = useError((v) => v.areSelectedModelsValid);
  let imageNotSucceededError = useError((v) => v.isSelectedImageSucceeded);
  if (missingAssetError) imageNotSucceededError = undefined;

  const promptEmptyError = useError((v) => v.prompt);

  useEffect(() => {
    setValues({
      areSelectedModelsValid,
    });
  }, [setValues, areSelectedModelsValid]);

  useEffect(() => {
    setValues({ selectedAssetUuid: selectedAsset?.uuid });
  }, [setValues, selectedAsset?.uuid]);

  useEffect(() => {
    setValues({
      isSelectedImageSucceeded: selectedAsset?.content.status === "SUCCEEDED",
    });
  }, [setValues, selectedAsset?.content.status]);

  useEffect(() => {
    setValues({ prompt: settings.prompt });
  }, [setValues, settings.prompt]);

  const previousImageUuid = useRef<string | null>();
  const [isWaitingForDescription, setIsWaitingForDescription] = useState(false);

  useEffect(() => {
    // XXX: the description and its loading state updates differently if the user selects another image.
    // To detect if the user has changed the selected image we use a ref previousImageUuid.
    // Case where the user is still on the same image:
    if (previousImageUuid.current === selectedAsset?.content.uuid) {
      if (isWaitingForDescription && initImageDescription?.description) {
        setSettings({
          prompt: initImageDescription.description,
        });
        setIsWaitingForDescription(false);
      }
    }
    // Case where the user selects another image:
    else {
      previousImageUuid.current = selectedAsset?.content.uuid;
      if (initImageDescription?.description) {
        setIsWaitingForDescription(false);
      } else if (!selectedAsset?.content.uuid) {
        setIsWaitingForDescription(false);
      } else {
        setIsWaitingForDescription(true);
      }
    }
  }, [
    isWaitingForDescription,
    initImageDescription?.description,
    selectedAsset?.content.uuid,
    setSettings,
  ]);

  return (
    <PanelLayout
      bottomChildren={
        <ActionsBar
          isLoading={isGenerationLoading}
          onPrimaryActionClick={submit}
          primaryActionButtonContent="Generate variations"
          intercomArticle="get_variations_of_your_image"
          errors={getErrorsFiltered([
            missingAssetError,
            imageNotSucceededError,
            modelsNotValidError,
          ])}
          errorsChildren={
            modelsNotValidError && (
              <ErrorButton
                onClick={() => {
                  modelActions.reset();
                }}
              >
                Reset model selection
              </ErrorButton>
            )
          }
        />
      }
    >
      <PromptSection
        sectionTitle="Variations description"
        prompt={settings.prompt}
        setPrompt={(newPrompt) => {
          setSettings({
            prompt: newPrompt,
          });
        }}
        placeholder="Describe what you want to generate"
        onEnterKeyDown={submit}
        errors={getErrorsFiltered([promptEmptyError])}
      />
      <PanelSection title="Creativity">
        <div className="flex-col-center gap-200">
          <Toggle
            items={VARIATION_CREATIVITY_STRENGTH_PRESETS.map((preset) => (
              <div key={preset} className="capitalize">
                {preset.toLowerCase()}
              </div>
            ))}
            index={VARIATION_CREATIVITY_STRENGTH_PRESETS.indexOf(
              settings.creativity_strength_preset,
            )}
            onIndexChange={(index) =>
              setSettings({
                creativity_strength_preset:
                  VARIATION_CREATIVITY_STRENGTH_PRESETS[index],
              })
            }
            className="w-full justify-between"
          />
          {settings.creativity_strength_preset === "CUSTOM" && (
            <div className="w-full flex-col gap-150">
              <SliderSubsection
                min={0}
                max={100}
                value={Math.round(settings.prompt_strength * 100)}
                onChange={(promptStrengthPercentage) =>
                  setSettings({
                    prompt_strength: promptStrengthPercentage / 100,
                    creativity_strength_preset: "CUSTOM",
                  })
                }
                sliderName="Creativity"
                sliderInformationSection={
                  <div className="flex-col body-md-default text-primary gap-400">
                    <div>
                      <span className="body-md-semibold">Creativity</span> -
                      Lower values will lead to similar images. Higher values
                      will lead to more creative images.
                    </div>
                    <UrlAnimation url="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/f4520585-172c-481c-ac9e-f81768893251" />
                    <LearnMoreLink
                      intercomArticle="get_variations_of_your_image"
                      className="self-end"
                    />
                  </div>
                }
              />
            </div>
          )}
        </div>
      </PanelSection>
      <ModelSelectionSection tool={tool} modelTypes={[STYLE_MODEL_TYPE]} />
    </PanelLayout>
  );
};
